import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '../../i18n';
import type { TranslationKey } from '../../i18n/types';
import { api } from '../../lib/api';
import { apiErrorMessage } from '../../lib/errors';
import { encodeQr, qrToSvgPath } from '../../lib/qr';
import { CHAIN_STATUS_KEY } from '../../lib/contest';
import type { CryptoInvoice, PaymentChainStatus } from '../../types';
import { Alert } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

/**
 * The crypto payment panel.
 *
 * HOW IT REPORTS STATUS: it renders whatever the SERVER says, and nothing else.
 * There is no optimistic "paid" state, no local confirmation counter, and no
 * success the browser decides. The only way this panel changes is by re-reading the
 * invoice, and the only way the invoice changes is the platform verifying a real
 * on-chain transfer.
 *
 * Polling is deliberately modest: every 15 seconds while the invoice is open,
 * stopped as soon as it reaches a final state, plus a manual "check now" button so
 * a participant who has just paid does not have to wait for a tick. Each refresh
 * costs blockchain API quota, so it is not something to do every second.
 */

const POLL_INTERVAL_MS = 15_000;

const ADDRESS_URI_PREFIX: Record<string, string> = {
  tron: 'tron:',
  ethereum: 'ethereum:',
};

type Tone = 'neutral' | 'brand' | 'success' | 'warning' | 'danger';

/** Tone and guidance per on-chain status, so the colour and the words agree. */
function statusTone(status: PaymentChainStatus): Tone {
  switch (status) {
    case 'confirmed':
    case 'overpaid':
      return 'success';
    case 'detected':
    case 'confirming':
      return 'brand';
    case 'awaiting_payment':
    case 'underpaid':
      return 'warning';
    case 'expired':
    case 'invalid':
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

const STATUS_HINT_KEY: Record<PaymentChainStatus, TranslationKey> = {
  awaiting_payment: 'pay.awaitingHint',
  detected: 'pay.detectedHint',
  confirming: 'pay.confirmingHint',
  confirmed: 'pay.confirmedHint',
  overpaid: 'pay.overpaidHint',
  underpaid: 'pay.underpaidHint',
  expired: 'pay.expiredHint',
  invalid: 'pay.invalidHint',
  failed: 'pay.failedHint',
};

/** Statuses where polling is still useful. */
const OPEN_STATUSES: readonly PaymentChainStatus[] = [
  'awaiting_payment',
  'detected',
  'confirming',
  'underpaid',
];

export function CryptoPaymentPanel({
  contestId,
  onStatusChange,
}: {
  contestId: string;
  onStatusChange?: () => void;
}) {
  const { t } = useTranslation();
  const [invoice, setInvoice] = useState<CryptoInvoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Read inside the polling closure without making the interval depend on it,
  // which would restart the timer on every status change.
  const statusRef = useRef<PaymentChainStatus | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const payload = await api.get<{ invoice: CryptoInvoice | null }>(
        `/contests/${contestId}/crypto-invoice`,
      );
      setInvoice(payload.invoice);
      statusRef.current = payload.invoice?.status ?? null;
    } catch (error_) {
      setError(apiErrorMessage(error_, t));
    } finally {
      setLoading(false);
    }
  }, [contestId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (statusRef.current === null) return;
      if (!OPEN_STATUSES.includes(statusRef.current)) {
        window.clearInterval(id);
        return;
      }
      void load();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  /** Ask the server to re-check the chain right now. */
  async function checkNow(): Promise<void> {
    setChecking(true);
    setError(null);
    try {
      const payload = await api.post<{ invoice: CryptoInvoice | null }>(
        `/contests/${contestId}/crypto-invoice/refresh`,
      );
      if (payload.invoice) {
        setInvoice(payload.invoice);
        statusRef.current = payload.invoice.status;
        onStatusChange?.();
      }
    } catch (error_) {
      setError(apiErrorMessage(error_, t));
    } finally {
      setChecking(false);
    }
  }

  async function copyAddress(): Promise<void> {
    if (!invoice) return;
    try {
      await navigator.clipboard.writeText(invoice.depositAddress);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied; the address is still on screen to copy.
    }
  }

  // Generated locally, so the deposit address never reaches an image service.
  const qr = useMemo(() => (invoice ? encodeQr(invoice.depositAddress) : null), [invoice]);

  if (loading) {
    return <div className="h-24 animate-pulse rounded-lg bg-surface-muted" aria-hidden="true" />;
  }

  if (!invoice) {
    return <Alert tone="info">{error ?? t('pay.notIssued')}</Alert>;
  }

  const tone = statusTone(invoice.status);
  const showProgress =
    invoice.requiredConfirmations > 0 &&
    (invoice.status === 'detected' || invoice.status === 'confirming');
  const progress = showProgress
    ? Math.min(100, Math.round((invoice.confirmations / invoice.requiredConfirmations) * 100))
    : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <span>{t('pay.title')}</span>
          <Badge tone={tone}>{t(CHAIN_STATUS_KEY[invoice.status])}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Alert tone={tone === 'danger' ? 'error' : tone === 'success' ? 'success' : 'info'}>
          {t(STATUS_HINT_KEY[invoice.status])}
        </Alert>

        {error && <Alert tone="error">{error}</Alert>}

        <div className="grid gap-4 sm:grid-cols-[auto_1fr]">
          {qr && (
            <div className="mx-auto flex flex-col items-center gap-2">
              <svg
                viewBox={`0 0 ${qr.size} ${qr.size}`}
                className="h-44 w-44 rounded-lg border border-border bg-white p-2"
                role="img"
                aria-label={t('pay.qrLabel')}
              >
                <path d={qrToSvgPath(qr)} fill="#000000" />
              </svg>
              <span className="text-xs text-muted">{t('pay.scanHint')}</span>
            </div>
          )}

          <dl className="flex min-w-0 flex-col gap-3 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted">{t('pay.amount')}</dt>
              <dd className="text-lg font-semibold text-fg">
                {invoice.amount} {invoice.asset}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide text-muted">{t('pay.network')}</dt>
              <dd className="text-fg">{invoice.networkLabel}</dd>
            </div>

            <div className="min-w-0">
              <dt className="text-xs uppercase tracking-wide text-muted">{t('pay.address')}</dt>
              {/* `break-all` keeps a long address inside a narrow phone layout
                  instead of forcing the whole page to scroll sideways. */}
              <dd className="break-all font-mono text-xs text-fg">{invoice.depositAddress}</dd>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={copyAddress}>
                  {copied ? t('pay.copied') : t('pay.copy')}
                </Button>
                <a
                  href={`${ADDRESS_URI_PREFIX[invoice.network] ?? ''}${invoice.depositAddress}`}
                  className="inline-flex"
                >
                  <Button size="sm" variant="ghost">
                    {t('pay.openInWallet')}
                  </Button>
                </a>
              </div>
            </div>

            <div>
              <dt className="text-xs uppercase tracking-wide text-muted">{t('pay.received')}</dt>
              <dd className="text-fg">
                {invoice.receivedAmount} / {invoice.amount} {invoice.asset}
              </dd>
            </div>

            {invoice.requiredConfirmations > 0 && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">
                  {t('pay.confirmations')}
                </dt>
                <dd className="text-fg">
                  {Math.min(invoice.confirmations, invoice.requiredConfirmations)} /{' '}
                  {invoice.requiredConfirmations}
                </dd>
                {showProgress && (
                  <div
                    className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-muted"
                    role="progressbar"
                    aria-valuenow={progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={t('pay.confirmations')}
                  >
                    <div
                      className="h-full bg-brand transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}
              </div>
            )}

            {invoice.txHash && (
              <div className="min-w-0">
                <dt className="text-xs uppercase tracking-wide text-muted">{t('pay.txHash')}</dt>
                <dd className="break-all font-mono text-xs text-fg">{invoice.txHash}</dd>
                {invoice.explorerUrl && (
                  <a
                    href={invoice.explorerUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-xs font-medium text-brand underline"
                  >
                    {t('pay.viewOnExplorer')}
                  </a>
                )}
              </div>
            )}

            {invoice.expiresAt && OPEN_STATUSES.includes(invoice.status) && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-muted">{t('pay.expires')}</dt>
                <dd className="text-fg">
                  {new Date(invoice.expiresAt).toLocaleString(undefined, {
                    dateStyle: 'short',
                    timeStyle: 'short',
                  })}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={checkNow} loading={checking} variant="outline" size="sm">
            {checking ? t('pay.checking') : t('pay.checkNow')}
          </Button>
          <p className="text-xs text-muted">{t('pay.autoCheck')}</p>
        </div>
      </CardContent>
    </Card>
  );
}

